import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { PRODUCT_CATEGORIES, CATEGORY_LABELS, allProducts } from '../../../constants/productsData';

const FeaturedCategories = () => {
  // Função para contar produtos por categoria
  const getProductCountForCategory = (categoryId) => {
    return allProducts.filter(product => product.categoryId === categoryId).length;
  };

  // Categorias que a Briza Móveis produz (com produtos e imagens disponíveis)
  const availableCategories = [
    {
      id: 0,
      name: CATEGORY_LABELS[0], // Sofás
      description: "Sofás e poltronas artesanais que combinam conforto e elegância para sua sala de estar.",
      image: "/assets/image-produtos/sofas/image01.jpeg",
      icon: "Sofa",
      pieces: getProductCountForCategory(0),
      featured: true,
      categoryValue: PRODUCT_CATEGORIES[0]
    },
    {
      id: 1,
      name: CATEGORY_LABELS[1], // Mesas
      description: "Mesas artesanais únicas para jantar, centro e escritório, cada uma com sua personalidade.",
      image: "/assets/image-produtos/mesas/image01.jpg",
      icon: "Table",
      pieces: getProductCountForCategory(1),
      featured: true,
      categoryValue: PRODUCT_CATEGORIES[1]
    },
    {
      id: 5,
      name: CATEGORY_LABELS[5], // Espreguiçadeiras
      description: "Espreguiçadeiras confortáveis para relaxar em casa ou na área externa.",
      image: "/assets/image-produtos/espreguicadeiras/image01.jpeg",
      icon: "Bed",
      pieces: getProductCountForCategory(5),
      featured: true,
      categoryValue: PRODUCT_CATEGORIES[5]
    },
    {
      id: 3,
      name: CATEGORY_LABELS[3], // Balanços
      description: "Balanços artesanais para relaxar e contemplar, perfeitos para áreas externas e internas.",
      image: "/assets/image-produtos/balancos/image01.jpeg",
      icon: "TreePine",
      pieces: getProductCountForCategory(3),
      featured: true,
      categoryValue: PRODUCT_CATEGORIES[3]
    },
    {
      id: 7,
      name: CATEGORY_LABELS[7], // Poltronas
      description: "Poltronas artesanais elegantes e confortáveis para criar ambientes acolhedores.",
      image: "/assets/image-produtos/poltrona/image01.jpeg",
      icon: "Armchair",
      pieces: getProductCountForCategory(7),
      featured: true,
      categoryValue: PRODUCT_CATEGORIES[7]
    }
  ];

  // Filtrar apenas categorias que têm produtos
  const categories = availableCategories.filter(category => category.pieces > 0);

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-muted rounded-full px-4 py-2 mb-6">
            <Icon name="Grid3X3" size={16} color="var(--color-primary)" />
            <span className="text-primary font-medium text-sm">Categorias</span>
          </div>
          
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Móveis para Cada Ambiente
          </h2>
          
          <p className="text-text-secondary text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Descubra nossa coleção completa de móveis artesanais, organizados por ambiente para facilitar sua escolha. 
            Cada categoria oferece peças únicas criadas especialmente para transformar seu espaço.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {categories?.map((category) => (
            <div
              key={category?.id}
              className="group relative overflow-hidden rounded-2xl bg-card border border-border hover:shadow-premium-lg transition-all duration-500 h-full flex flex-col"
            >
              {/* Image Container */}
              <div className="relative overflow-hidden h-48 flex-shrink-0">
                <Image
                  src={category?.image}
                  alt={category?.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300"></div>
                
                {/* Featured Badge */}
                {category?.featured && (
                  <div className="absolute top-4 left-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-medium">
                    Destaque
                  </div>
                )}

                {/* Pieces Count */}
                <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                  {category?.pieces} peças
                </div>

                {/* Category Icon */}
                <div className="absolute bottom-4 left-4 w-12 h-12 bg-primary/20 backdrop-blur-sm border border-primary/30 rounded-full flex items-center justify-center">
                  <Icon name={category?.icon} size={24} color="white" />
                </div>
              </div>

              {/* Content */}
              <div className="p-4 flex flex-col flex-grow">
                <h3 className="font-playfair text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
                  {category?.name}
                </h3>
                
                <p className="text-text-secondary text-sm leading-relaxed mb-4 flex-grow">
                  {category?.description}
                </p>

                {/* CTA */}
                <Link
                  to={`/interactive-catalog?category=${category.categoryValue}`}
                  className="inline-flex items-center space-x-2 text-primary font-medium hover:text-trust transition-colors duration-300 text-sm"
                >
                  <span>Explorar Categoria</span>
                  <Icon name="ArrowRight" size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <Button
            variant="outline"
            size="lg"
            iconName="Grid3X3"
            iconPosition="left"
            className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          >
            <Link to="/interactive-catalog">Ver Catálogo Completo</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedCategories;